<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisTabs from 'dashboard/components-next/LaisTabs.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisCardSkeleton from 'dashboard/components-next/LaisCardSkeleton.vue';
import LaisErrorState from 'dashboard/components-next/LaisErrorState.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import OpenHouseAPI from 'dashboard/api/openHouse';
import { presentEvent, eventStatusTone } from '../utils/openHousePresenter';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');

const STATUS_TABS = {
  all: undefined,
  active: 'draft,scheduled,open',
  closed: 'closed',
  canceled: 'canceled',
};

const isLoading = ref(true);
const hasError = ref(false);
const hasPermissionError = ref(false);
const events = ref([]);

const createOpen = ref(false);
const createLoading = ref(false);
const createError = ref('');
const form = ref({
  title: '',
  imovel_listing_id: '',
  starts_at: '',
  ends_at: '',
  capacity: '',
});

const activeTab = computed({
  get: () => {
    const q = String(route.query.status || 'active');
    return Object.keys(STATUS_TABS).includes(q) ? q : 'active';
  },
  set: value => {
    router.replace({ query: { ...route.query, status: value } });
  },
});

const fetchEvents = async () => {
  isLoading.value = true;
  hasError.value = false;
  hasPermissionError.value = false;
  try {
    const { data } = await OpenHouseAPI.get({
      status: STATUS_TABS[activeTab.value],
    });
    events.value = (data?.payload || []).map(event => presentEvent(event));
  } catch (error) {
    if (error?.response?.status === 403) hasPermissionError.value = true;
    else hasError.value = true;
    events.value = [];
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchEvents);
watch(activeTab, fetchEvents);

const tabs = computed(() => [
  { id: 'all', label: t('VISITS.OPEN_HOUSE.TABS.ALL') },
  { id: 'active', label: t('VISITS.OPEN_HOUSE.TABS.ACTIVE') },
  { id: 'closed', label: t('VISITS.OPEN_HOUSE.TABS.CLOSED') },
  { id: 'canceled', label: t('VISITS.OPEN_HOUSE.TABS.CANCELED') },
]);

const isEmpty = computed(
  () => !isLoading.value && !hasError.value && events.value.length === 0
);

const showPath = event =>
  frontendURL(`accounts/${accountId.value}/open-house/${event.id}`);

const canSubmitCreate = computed(
  () =>
    form.value.title.trim() &&
    form.value.imovel_listing_id &&
    form.value.starts_at &&
    form.value.ends_at
);

const submitCreate = async () => {
  if (!canSubmitCreate.value) return;
  createLoading.value = true;
  createError.value = '';
  try {
    const { data } = await OpenHouseAPI.create({
      title: form.value.title.trim(),
      imovel_listing_id: Number(form.value.imovel_listing_id),
      starts_at: form.value.starts_at,
      ends_at: form.value.ends_at || undefined,
      capacity: form.value.capacity ? Number(form.value.capacity) : undefined,
    });
    createOpen.value = false;
    form.value = {
      title: '',
      imovel_listing_id: '',
      starts_at: '',
      ends_at: '',
      capacity: '',
    };
    if (data?.payload?.id) router.push(showPath(data.payload));
    else fetchEvents();
  } catch (error) {
    createError.value =
      error?.response?.data?.errors?.join(', ') ||
      t('VISITS.OPEN_HOUSE.CREATE.ERROR');
  } finally {
    createLoading.value = false;
  }
};

const capacityLabel = event =>
  event.capacity.unlimited
    ? t('VISITS.OPEN_HOUSE.CAPACITY_UNLIMITED')
    : t('VISITS.OPEN_HOUSE.CAPACITY_COUNT', {
        occupied: event.capacity.occupied,
        capacity: event.capacity.capacity,
      });

const eventStatusLabel = status => {
  if (status === 'scheduled') return t('VISITS.OPEN_HOUSE.STATUS.SCHEDULED');
  if (status === 'open') return t('VISITS.OPEN_HOUSE.STATUS.OPEN');
  if (status === 'closed') return t('VISITS.OPEN_HOUSE.STATUS.CLOSED');
  if (status === 'canceled') return t('VISITS.OPEN_HOUSE.STATUS.CANCELED');
  return t('VISITS.OPEN_HOUSE.STATUS.DRAFT');
};
</script>

<template>
  <div
    class="flex h-full min-h-0 flex-col overflow-y-auto bg-lais-bg md:overflow-hidden"
  >
    <LaisHeader
      sticky
      :title="t('VISITS.OPEN_HOUSE.TITLE')"
      :helper="t('VISITS.OPEN_HOUSE.HELPER')"
    />

    <div
      class="min-h-0 flex-1 overflow-auto px-4 pb-24 pt-4 sm:px-6 sm:pb-8 lg:px-8 max-md:flex-none max-md:overflow-visible"
    >
      <div class="mx-auto flex w-full max-w-4xl flex-col gap-5">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <LaisTabs
            v-model="activeTab"
            :tabs="tabs"
            :aria-label="t('VISITS.OPEN_HOUSE.STATUS_FILTER')"
            :lazy="false"
          />
          <LaisPrimaryButton
            size="sm"
            icon="i-lucide-plus"
            @click="createOpen = true"
          >
            {{ t('VISITS.OPEN_HOUSE.CREATE.CTA') }}
          </LaisPrimaryButton>
        </div>

        <LaisCardSkeleton v-if="isLoading" :count="3" mode="row" />

        <LaisErrorState
          v-else-if="hasPermissionError"
          status="403"
          icon="i-lucide-lock"
          :title="t('VISITS.OPEN_HOUSE.PERMISSION_TITLE')"
          :description="t('VISITS.OPEN_HOUSE.PERMISSION_DESCRIPTION')"
        />

        <LaisErrorState
          v-else-if="hasError"
          :title="t('VISITS.OPEN_HOUSE.ERROR_TITLE')"
          :description="t('VISITS.OPEN_HOUSE.ERROR_DESCRIPTION')"
          show-action
          :action-label="t('VISITS.OPEN_HOUSE.RETRY')"
          @action="fetchEvents"
        />

        <div
          v-else-if="isEmpty"
          class="flex flex-col items-center gap-3 rounded-lais border border-dashed border-lais-border bg-lais-bg-soft px-4 py-14 text-center"
        >
          <span
            class="i-lucide-door-open size-8 text-lais-text-muted"
            aria-hidden="true"
          />
          <p class="text-lais-body-md font-semibold text-lais-text">
            {{ t('VISITS.OPEN_HOUSE.EMPTY_TITLE') }}
          </p>
          <p class="max-w-md text-lais-body-sm text-lais-text-muted">
            {{ t('VISITS.OPEN_HOUSE.EMPTY_DESCRIPTION') }}
          </p>
          <LaisPrimaryButton
            size="sm"
            icon="i-lucide-plus"
            @click="createOpen = true"
          >
            {{ t('VISITS.OPEN_HOUSE.CREATE.CTA') }}
          </LaisPrimaryButton>
        </div>

        <ul v-else class="flex flex-col gap-3" role="list">
          <li v-for="event in events" :key="event.id">
            <LaisCard interactive :href="showPath(event)" density="tight">
              <div
                class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
              >
                <div class="min-w-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <LaisStatusBadge
                      :label="eventStatusLabel(event.status)"
                      :tone="eventStatusTone(event.status)"
                    />
                    <span
                      class="text-lais-label-sm text-lais-text-muted tabular-nums"
                    >
                      {{ capacityLabel(event) }}
                    </span>
                  </div>
                  <p
                    class="mt-1.5 truncate text-lais-body-md font-semibold text-lais-text"
                  >
                    {{ event.title || t('VISITS.OPEN_HOUSE.UNTITLED') }}
                  </p>
                  <p
                    v-if="event.startsAt"
                    class="text-lais-body-sm text-lais-text-muted"
                  >
                    {{
                      new Date(event.startsAt).toLocaleString('pt-BR', {
                        timeZone: 'UTC',
                      })
                    }}
                  </p>
                </div>
                <span
                  class="i-lucide-chevron-right size-5 shrink-0 text-lais-text-muted"
                  aria-hidden="true"
                />
              </div>
            </LaisCard>
          </li>
        </ul>
      </div>
    </div>

    <!-- Create modal -->
    <LaisModal
      :show="createOpen"
      :title="t('VISITS.OPEN_HOUSE.CREATE.TITLE')"
      @close="createOpen = false"
    >
      <form class="flex flex-col gap-4" @submit.prevent="submitCreate">
        <LaisField :label="t('VISITS.OPEN_HOUSE.CREATE.EVENT_TITLE')" required>
          <input
            v-model.trim="form.title"
            type="text"
            class="h-10 w-full rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-md text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
          />
        </LaisField>
        <LaisField :label="t('VISITS.OPEN_HOUSE.CREATE.LISTING_ID')" required>
          <input
            v-model="form.imovel_listing_id"
            type="number"
            min="1"
            class="h-10 w-full rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-md text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
          />
        </LaisField>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <LaisField :label="t('VISITS.OPEN_HOUSE.CREATE.STARTS_AT')" required>
            <input
              v-model="form.starts_at"
              type="datetime-local"
              class="h-10 w-full rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-md text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
            />
          </LaisField>
          <LaisField :label="t('VISITS.OPEN_HOUSE.CREATE.ENDS_AT')">
            <input
              v-model="form.ends_at"
              type="datetime-local"
              class="h-10 w-full rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-md text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
            />
          </LaisField>
        </div>
        <LaisField
          :label="t('VISITS.OPEN_HOUSE.CREATE.CAPACITY')"
          :helper="t('VISITS.OPEN_HOUSE.CREATE.CAPACITY_HELPER')"
        >
          <input
            v-model="form.capacity"
            type="number"
            min="1"
            class="h-10 w-full rounded-lais border border-lais-border-soft bg-lais-bg-card px-3 text-lais-body-md text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
          />
        </LaisField>

        <p
          v-if="createError"
          class="text-lais-body-sm text-lais-danger"
          role="alert"
        >
          {{ createError }}
        </p>

        <div class="flex justify-end gap-2 pt-1">
          <LaisPrimaryButton
            variant="ghost"
            type="button"
            @click="createOpen = false"
          >
            {{ t('VISITS.OPEN_HOUSE.CREATE.CANCEL') }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            type="submit"
            :disabled="!canSubmitCreate"
            :is-loading="createLoading"
          >
            {{ t('VISITS.OPEN_HOUSE.CREATE.SUBMIT') }}
          </LaisPrimaryButton>
        </div>
      </form>
    </LaisModal>
  </div>
</template>
